"use client";
import React, { useEffect, useState } from "react";
import Link from "next/link";
import { AnimatePresence, motion } from "framer-motion";
import Image from "next/image";
import { IoIosArrowDown } from "react-icons/io";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/Accordion";
import { usePathname } from "next/navigation";
import DesktopMenu from "./DesktopMenu";
import { Menubar } from "@/components/ui/Menubar";
import {
  buildMenuData,
  isRouteActive,
  isMenuItemOrChildActive,
  MenuItemType,
} from "./utils";
import { DrupalNode } from "next-drupal";

const Header: React.FC<{ data: DrupalNode[] }> = ({ data }) => {
  const [isMobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);
  const [openAccordions, setOpenAccordions] = useState<Set<string>>(new Set());

  // Transform all routes at once
  const menuData = buildMenuData(data[0]);
  const currentPath = usePathname();
  const [currentHash, setCurrentHash] = useState<string>("");

  useEffect(() => {
    const handleHashChange = () => {
      if (typeof window !== "undefined") {
        const hash = window.location.hash || "";
        setCurrentHash((prev) => (prev !== hash ? hash : prev));
      }
    };

    handleHashChange();

    window.addEventListener("hashchange", handleHashChange);
    window.addEventListener("popstate", handleHashChange);
    window.addEventListener("click", handleHashChange);

    const interval = setInterval(handleHashChange, 100);

    return () => {
      window.removeEventListener("hashchange", handleHashChange);
      window.removeEventListener("popstate", handleHashChange);
      window.removeEventListener("click", handleHashChange);
      clearInterval(interval);
    };
  }, [currentPath]);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!isMobileMenuOpen);
  };
  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? "hidden" : "auto";

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isMobileMenuOpen]);

  // Close mobile menu when screen size changes to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1201) {
        // desktop breakpoint
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Function to get all parent keys that should be open based on real-time current path and hash
  const getParentKeys = (
    items: Record<string, MenuItemType>,
    currentKey: string = "",
    parents: string[] = [],
  ): string[] => {
    for (const [key, item] of Object.entries(items)) {
      const fullKey = currentKey ? `${currentKey}-${key}` : key;

      if (isRouteActive(item.href, currentPath, currentHash, menuData)) {
        return [...parents, fullKey];
      }

      if (item.items) {
        const result = getParentKeys(item.items, fullKey, [
          ...parents,
          fullKey,
        ]);
        if (result.length > 0) {
          return result;
        }
      }
    }
    return [];
  };

  // Initialize open accordions based on current path & hash
  useEffect(() => {
    if (menuData) {
      const parentKeys = getParentKeys(menuData);
      setOpenAccordions(new Set(parentKeys));
    }
  }, [currentPath, currentHash]);

  if (currentPath?.startsWith("/toolkit")) return null;

  const renderMenu = (
    items: Record<string, MenuItemType>,
    level = 0,
    parentKey = "",
  ) => {
    return Object.entries(items).map(([key, item], index: number, arr) => {
      const iconPath = item.icon?.toLowerCase?.() || "";
      const isComingSoon = item.title?.includes("Coming soon") ?? false;
      const comingSoonClass = isComingSoon ? "coming-soon-item" : "";
      const isIndiaItem =
        item.title?.toLowerCase?.().includes("india") ?? false;
      const isAseanItem =
        item.title?.toLowerCase?.().includes("southeast asia") ?? false;
      const isKenyaItem =
        item.title?.toLowerCase?.().includes("kenya") ?? false;
      const isCountryItem =
        iconPath.includes("/flags/") ||
        iconPath.includes("flag") ||
        (item.href?.includes("/country-engagement") ?? false);
      const itemIconClass = [
        comingSoonClass,
        isCountryItem ? "country-flag-item" : "",
        isIndiaItem ? "india-flag-item" : "",
        isAseanItem ? "asean-flag-item" : "",
        isKenyaItem ? "kenya-flag-item" : "",
      ]
        .filter(Boolean)
        .join(" ");
      const childItems =
        item.items && Object.keys(item.items || {}).length > 0
          ? item.items
          : undefined;
      const hasSubItems = Boolean(childItems);
      const fullKey = parentKey ? `${parentKey}-${key}` : key;
      const isOpen = openAccordions.has(fullKey);
      const isLast = index === arr.length - 1;
      const isCurrentPage = isRouteActive(
        item.href,
        currentPath,
        currentHash,
        menuData
      );

      const isChildActive = item.items
        ? Object.values(item.items).some((child) =>
            isMenuItemOrChildActive(child, currentPath, currentHash, menuData),
          )
        : false;

      const isHighlighted = isCurrentPage || isChildActive;

      const paddingLeft = 15 + level * 20;

      if (!hasSubItems || !childItems) {
        return (
          <Link
            key={key}
            href={item.href || "#"}
            className={`border-b border-[#00335059] text-small py-2 pr-6 flex items-center gap-2 ${
              isComingSoon
                ? ""
                : "hover:underline hover:underline-offset-4 hover:decoration-[#003350] hover:text-[#003350]"
            } ${
              isCurrentPage
                ? "bg-[#8EBCE5] !text-[#003350] underline underline-offset-4 decoration-[#003350] active-menu-wrap font-semibold"
                : "text-[#003350] font-normal"
            } ${isComingSoon ? "opacity-50 cursor-default" : ""} ${
              itemIconClass || ""
            }`}
            style={{ paddingLeft: `${paddingLeft}px` }}
            aria-disabled={isComingSoon}
            tabIndex={isComingSoon ? -1 : 0}
            onClick={() => {
              if (isComingSoon) return;
              setMobileMenuOpen(false);
            }}
          >
            {item.icon && (
              <Image
                src={item.icon}
                height={18}
                width={18}
                alt="icon"
                className="w-[18px] h-[18px] mr-2"
              />
            )}
            {item.title}
          </Link>
        );
      }

      const handleAccordionValueChange = (value: string) => {
        if (isComingSoon) return;
        setOpenAccordions((prev) => {
          const newSet = new Set(prev);
          if (value) {
            newSet.add(fullKey);
          } else {
            newSet.delete(fullKey);
          }
          return newSet;
        });
      };

      return (
        <Accordion
          key={key}
          type="single"
          collapsible
          value={isOpen ? fullKey : undefined}
          onValueChange={handleAccordionValueChange}
        >
          <AccordionItem value={fullKey}>
            <AccordionTrigger
              noArrow
              disabled={isComingSoon}
              aria-disabled={isComingSoon}
              className={`${
                isLast ? "" : "border-b border-[#00335059]"
              } text-small flex items-center justify-between py-2 pr-6 ${
                isComingSoon
                  ? ""
                  : "hover:text-[#003350] hover:underline hover:underline-offset-4 hover:decoration-[#003350]"
              } ${
                isHighlighted
                  ? "bg-[#8EBCE5] !text-[#003350] underline underline-offset-4 decoration-[#003350] font-semibold active-menu-wrap"
                  : "text-[#003350] font-normal"
              } ${isComingSoon ? "opacity-50 cursor-default" : ""}`}
              style={{ paddingLeft: `${paddingLeft}px` }}
            >
              <Link
                key={key}
                href={item.href || "#"}
                className={`flex items-center gap-2 pr-2 ${
                  isComingSoon ? "pointer-events-none" : ""
                } ${itemIconClass}`.trim()}
                aria-disabled={isComingSoon}
                tabIndex={isComingSoon ? -1 : 0}
                onClick={(event) => {
                  if (isComingSoon) {
                    event.preventDefault();
                    return;
                  }
                  setMobileMenuOpen(false);
                }}
              >
                {item.icon && (
                  <Image
                    src={item.icon}
                    height={18}
                    width={18}
                    alt="icon"
                    className="w-[18px] h-[18px] mr-2"
                  />
                )}
                <span
                  className={`text-[#003350] font-poppins text-small ${
                    isHighlighted
                      ? "!text-[#003350] underline underline-offset-4 decoration-[#003350] active-menu-wrap font-semibold"
                      : "text-[#003350] font-normal"
                  }`}
                >
                  {item.title}
                </span>
              </Link>
              <IoIosArrowDown
                className={`ml-1 text-md transition-transform duration-200 ${
                  isOpen ? "rotate-180" : ""
                } ${isComingSoon ? "opacity-50" : ""}`}
              />
            </AccordionTrigger>

            <AccordionContent className="pb-0">
              {childItems && renderMenu(childItems, level + 1, fullKey)}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      );
    });
  };

  return (
    <>
      <motion.div
        className="app-menu landing-header-zIndex"
        style={{
          boxShadow: "0px 11px 12.9px 0px #0000000D",
          backdropFilter: "blur(12.600000381469727px)",
        }}
      >
        <Menubar
          className={`border-none justify-between flex min-h-[80px]`}
          role="toolbar"
        >
          <div className="mini-container">
            <div className="flex justify-between w-full items-center">
              <div>
                <Link href="/" className="cursor-pointer z-50">
                  <Image
                    className="cursor-pointer"
                    src="/static/images/main-logo.svg"
                    width={255}
                    height={45}
                    alt="app logo"
                  />
                </Link>
              </div>
              <div className="hidden desktop:block">
                <DesktopMenu menuData={menuData} />
              </div>

              {/* toggle */}
              <button
                className="block desktop:hidden mobile-menu-toggle"
                onClick={toggleMobileMenu}
              >
                {!isMobileMenuOpen ? (
                  <Image
                    src="/static/images/bar-icon.svg"
                    alt="bar"
                    height={23}
                    width={34}
                    objectFit="cover"
                    className="w-[34px] h-[23px]"
                  />
                ) : (
                  <Image
                    src="/static/images/gradient-close.svg"
                    alt="bar"
                    height={23}
                    width={34}
                    objectFit="cover"
                    className="w-[34px] h-[23px]"
                  />
                )}
              </button>
            </div>
          </div>
        </Menubar>
        {/* mobile menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              key="mobile-menu"
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -5 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="bg-white mt-[-2px] card-shadow absolute top-[80px] w-full h-[calc(100vh_-82px)] overflow-hidden"
            >
              <div className="popup-menu h-full w-full overflow-auto border-t border-[#66393959]">
                {renderMenu(menuData)}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </>
  );
};

export default Header;
